import { useCallback, useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getDeviceSessions } from "../api/devices";

// "Sessions" tab of the device page: the robot's cleaning sessions (written by
// rc-device-poller), newest first. Loads only when the tab is opened, then one
// page at a time as the list is scrolled (or with "Load more").

const PAGE_SIZE = 20;

const SESSION_STATUS_COLORS = {
  completed: "success",
  running: "primary",
  requested: "primary",
  undocking: "primary",
  paused: "warning",
  held: "warning",
  cancelled: "default",
  failed: "error",
  interrupted: "error",
};

const ZONE_STATUS_COLORS = {
  done: "success",
  pending: "default",
  dispatched: "primary",
  cleaning: "primary",
  paused: "warning",
  interrupted: "error",
  failed: "error",
  skipped: "default",
};

// Readable text for the reason codes the poller stores
const REASON_TEXT = {
  CLEAN_STOP_LOCAL: "Stopped at the robot",
  CLEAN_STOP_REMOTE: "Stopped from the controls",
  STOPPED_BEFORE_COMPLETE: "Stopped before the zone was finished",
  RETURNED_TO_DOCK_BEFORE_COMPLETE: "Returned to the dock before finishing",
  STOP_NOT_CONFIRMED: "Stop sent but the robot kept cleaning",
  NO_PROGRESS: "Robot did not start cleaning",
  UNABLE_TO_GO_HOME: "Could not return to the dock",
  PROGRESS_ESTIMATE: "Finished (estimated from progress)",
};

const humanise = (code) =>
  String(code)
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase());

export function formatReason(reason) {
  if (!reason) return "";
  const text = String(reason);
  if (text.startsWith("PARTIAL_")) {
    return `Partly cleaned: ${humanise(text.slice("PARTIAL_".length)).toLowerCase()}`;
  }
  if (REASON_TEXT[text]) return REASON_TEXT[text];
  // Free text such as "stop by jm" or "STOP_REQUESTED: dock by jm" is shown as is
  return /^[A-Z0-9_]+$/.test(text) ? humanise(text) : text;
}

function formatDateTime(value) {
  if (!value) return "–";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "–";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatDuration(start, end) {
  if (!start) return "–";
  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : Date.now();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) {
    return "–";
  }
  const minutes = Math.round((endMs - startMs) / 60000);
  if (minutes < 1) return "< 1 min";
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours > 0 ? `${hours} h ${rest} min` : `${rest} min`;
}

function startedByText(session) {
  if (session.initiated_by_label) return session.initiated_by_label;
  if (session.initiated_by_type === "automation_service") return "Automation";
  return session.initiated_by_type || "–";
}

function SessionZones({ zones }) {
  if (!zones?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No zones recorded for this session.
      </Typography>
    );
  }
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Zone</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Coverage</TableCell>
            <TableCell>Time</TableCell>
            <TableCell>Reason</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {zones.map((zone) => {
            const partial = String(zone.outcome_reason || "").startsWith("PARTIAL_");
            return (
              <TableRow key={zone.pk_id}>
                <TableCell>{Number(zone.execution_order) + 1}</TableCell>
                <TableCell>
                  <Typography variant="body2">{zone.zone_name}</Typography>
                  {zone.zone_group_display_name && (
                    <Typography variant="caption" color="text.secondary">
                      {zone.zone_group_display_name}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={partial ? "done (partly)" : zone.status}
                    color={partial ? "warning" : ZONE_STATUS_COLORS[zone.status] || "default"}
                    variant={zone.status === "pending" ? "outlined" : "filled"}
                  />
                  {Number(zone.attempts) > 1 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block" }}
                    >
                      {zone.attempts} attempts
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {zone.last_extent != null ? `${zone.last_extent}%` : "–"}
                </TableCell>
                <TableCell>
                  {zone.started_at || zone.dispatched_at
                    ? formatDuration(
                        zone.started_at || zone.dispatched_at,
                        zone.ended_at,
                      )
                    : "–"}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatReason(zone.outcome_reason)}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function SessionRow({ session }) {
  const partialZones = (session.zones || []).filter((z) =>
    String(z.outcome_reason || "").startsWith("PARTIAL_"),
  ).length;
  const isActive = ["requested", "undocking", "running", "paused", "held"].includes(
    session.status,
  );

  return (
    <Accordion
      disableGutters
      variant="outlined"
      // Zone tables are only rendered for the sessions someone opens
      slotProps={{ transition: { unmountOnExit: true } }}
      sx={{ "&:not(:last-of-type)": { borderBottom: 0 } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            columnGap: 2,
            rowGap: 0.5,
            width: "100%",
            pr: 1,
          }}
        >
          <Box sx={{ minWidth: 160 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatDateTime(session.started_at || session.requested_at)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isActive ? "Running for " : ""}
              {formatDuration(
                session.started_at || session.requested_at,
                session.ended_at,
              )}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={session.status}
            color={SESSION_STATUS_COLORS[session.status] || "default"}
          />
          <Typography variant="body2">
            {session.completed_zones}/{session.total_zones} zones
          </Typography>
          {partialZones > 0 && (
            <Tooltip title="Zones the robot partly cleaned because an area was blocked or unreachable">
              <Chip
                size="small"
                variant="outlined"
                color="warning"
                label={`${partialZones} partly`}
              />
            </Tooltip>
          )}
          {session.cleaning_preset_name && (
            <Typography variant="body2" color="text.secondary">
              {session.cleaning_preset_name}
            </Typography>
          )}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: { sm: "auto" } }}
          >
            {startedByText(session)}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: 1,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Requested
            </Typography>
            <Typography variant="body2">
              {formatDateTime(session.requested_at)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Ended
            </Typography>
            <Typography variant="body2">
              {session.ended_at ? formatDateTime(session.ended_at) : "–"}
              {session.ended_by_label ? ` by ${session.ended_by_label}` : ""}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Reason
            </Typography>
            <Typography variant="body2">
              {formatReason(session.status_reason) || "–"}
            </Typography>
          </Box>
        </Box>
        {session.error_message && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {session.error_message}
          </Alert>
        )}
        <SessionZones zones={session.zones} />
      </AccordionDetails>
    </Accordion>
  );
}

export default function DeviceSessionsTab({ rcDeviceId }) {
  const [sessions, setSessions] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const sentinelRef = useRef(null);
  const loadingRef = useRef(false);

  const loadPage = useCallback(
    async ({ reset = false } = {}) => {
      if (!rcDeviceId || loadingRef.current) return;
      loadingRef.current = true;
      setLoading(true);
      setError(null);
      try {
        const page = await getDeviceSessions(rcDeviceId, {
          before: reset ? null : nextBefore,
          limit: PAGE_SIZE,
        });
        setSessions((prev) =>
          reset ? page.sessions || [] : [...prev, ...(page.sessions || [])],
        );
        setNextBefore(page.nextBefore ?? null);
        setHasMore(!!page.hasMore);
        setLoaded(true);
      } catch (err) {
        setError(err.message || "Failed to load sessions");
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [rcDeviceId, nextBefore],
  );

  // First page when the tab is first shown (this component is only mounted then)
  useEffect(() => {
    loadPage({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rcDeviceId]);

  // Load the next page when the bottom of the list scrolls into view
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || typeof IntersectionObserver === "undefined") {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadPage();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadPage]);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2">Cleaning sessions</Typography>
          <Typography variant="caption" color="text.secondary">
            Newest first. Sessions are kept for 3 months.
          </Typography>
        </Box>
        <Tooltip title="Reload">
          <span>
            <IconButton
              size="small"
              onClick={() => loadPage({ reset: true })}
              disabled={loading}
              aria-label="Reload sessions"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => loadPage({ reset: !loaded })}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {loaded && sessions.length === 0 && !error && (
        <Typography variant="body2" color="text.secondary">
          No cleaning sessions recorded for this device yet.
        </Typography>
      )}

      {sessions.map((session) => (
        <SessionRow key={session.pk_id} session={session} />
      ))}

      <Box ref={sentinelRef} sx={{ height: 1 }} />

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {hasMore && !loading && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
          <Button variant="outlined" size="small" onClick={() => loadPage()}>
            Load more
          </Button>
        </Box>
      )}

      {loaded && !hasMore && sessions.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", textAlign: "center", pt: 2 }}
        >
          {sessions.length} session{sessions.length === 1 ? "" : "s"} shown
        </Typography>
      )}
    </Box>
  );
}
